import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Plus, Trash2, Mail, Library, Eye } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import { RichTextEditor } from "@/components/proposals/RichTextEditor";
import EmailTemplateGallery from "./EmailTemplateGallery";
import { EmailTemplate } from "@/data/emailTemplates";

interface SequenceStep {
  id?: string;
  step_number: number;
  step_type: string;
  delay_days: number;
  delay_hours: number;
  subject?: string;
  body?: string;
}

interface SequenceBuilderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sequence: any | null;
  onSaved: () => void;
}

export const SequenceBuilderDialog = ({
  open,
  onOpenChange,
  sequence,
  onSaved,
}: SequenceBuilderDialogProps) => {
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [steps, setSteps] = useState<SequenceStep[]>([
    {
      step_number: 1,
      step_type: "email",
      delay_days: 0,
      delay_hours: 0,
      subject: "",
      body: "",
    },
  ]);
  const [showTemplateGallery, setShowTemplateGallery] = useState(false);
  const [previewStep, setPreviewStep] = useState<number | null>(null);
  
  const { user, orgId } = useAuth();

  useEffect(() => {
    if (sequence) {
      setName(sequence.name);
      setDescription(sequence.description || "");
      loadSteps(sequence.id);
    } else {
      // Reset for new sequence
      setName("");
      setDescription("");
      setSteps([
        {
          step_number: 1,
          step_type: "email",
          delay_days: 0,
          delay_hours: 0,
          subject: "",
          body: "",
        },
      ]);
    }
  }, [sequence, open]);

  const loadSteps = async (sequenceId: string) => {
    try {
      const { data, error } = await supabase
        .from('sequence_steps')
        .select('*')
        .eq('sequence_id', sequenceId)
        .order('step_number', { ascending: true });

      if (error) throw error;
      if (data && data.length > 0) {
        setSteps(data);
      }
    } catch (error) {
      console.error("Error loading steps:", error);
    }
  };

  const addStep = () => {
    setSteps([
      ...steps,
      {
        step_number: steps.length + 1,
        step_type: "email",
        delay_days: 1,
        delay_hours: 0,
        subject: "",
        body: "",
      },
    ]);
  };

  const removeStep = (index: number) => {
    if (steps.length === 1) {
      toast.error("Sequence must have at least one step");
      return;
    }
    const newSteps = steps.filter((_, i) => i !== index);
    // Renumber steps
    newSteps.forEach((step, i) => {
      step.step_number = i + 1;
    });
    setSteps(newSteps);
  };

  const updateStep = (index: number, field: keyof SequenceStep, value: any) => {
    const newSteps = [...steps];
    newSteps[index] = { ...newSteps[index], [field]: value };
    setSteps(newSteps);
  };

  const handleTemplateSelect = (template: EmailTemplate) => {
    setName(template.name);
    setDescription(template.description);
    setSteps(template.steps.map(step => ({
      step_number: step.step,
      step_type: "email",
      delay_days: step.delayDays,
      delay_hours: step.delayHours || 0,
      subject: step.subject,
      body: step.body,
    })));
    toast.success("Template loaded! You can now customize it.");
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error("Please enter a sequence name");
      return;
    }

    if (steps.some(s => s.step_type === 'email' && (!s.subject?.trim() || !s.body?.trim()))) {
      toast.error("All email steps must have a subject and body");
      return;
    }

    setLoading(true);
    try {
      if (!user || !orgId) throw new Error("Not authenticated");

      let sequenceId = sequence?.id;

      if (sequence) {
        // Update existing sequence
        const { error: updateError } = await supabase
          .from('email_sequences')
          .update({
            name,
            description,
            total_steps: steps.length,
          })
          .eq('id', sequenceId);

        if (updateError) throw updateError;

        // Delete existing steps
        await supabase
          .from('sequence_steps')
          .delete()
          .eq('sequence_id', sequenceId);
      } else {
        // Create new sequence
        const { data: newSequence, error: insertError } = await supabase
          .from('email_sequences')
          .insert({
            org_id: orgId,
            name,
            description,
            status: 'draft',
            total_steps: steps.length,
            created_by: user.id,
          })
          .select()
          .single();

        if (insertError) throw insertError;
        sequenceId = newSequence.id;
      }

      // Insert all steps
      const stepsToInsert = steps.map(step => ({
        sequence_id: sequenceId,
        step_number: step.step_number,
        step_type: step.step_type,
        delay_days: step.delay_days,
        delay_hours: step.delay_hours,
        subject: step.subject || null,
        body: step.body || null,
      }));

      const { error: stepsError } = await supabase
        .from('sequence_steps')
        .insert(stepsToInsert);

      if (stepsError) throw stepsError;

      toast.success(sequence ? "Sequence updated!" : "Sequence created!");
      onSaved();
    } catch (error) {
      console.error("Error saving sequence:", error);
      toast.error("Failed to save sequence");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <EmailTemplateGallery
        open={showTemplateGallery}
        onOpenChange={setShowTemplateGallery}
        onSelectTemplate={handleTemplateSelect}
      />

      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{sequence ? "Edit" : "Create"} Email Sequence</DialogTitle>
          </DialogHeader>

          <div className="space-y-6">
            <div className="space-y-4">
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowTemplateGallery(true)}
                  className="gap-2"
                >
                  <Library className="w-4 h-4" />
                  Browse Templates
                </Button>
              </div>

              <div className="space-y-2">
                <Label htmlFor="name">Sequence Name</Label>
                <Input
                  id="name"
                  placeholder="e.g., New Lead Follow-up"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Description (Optional)</Label>
                <Input
                  id="description"
                  placeholder="Describe this sequence..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">Sequence Steps</h3>
                <Button onClick={addStep} size="sm" className="gap-2">
                  <Plus className="w-4 h-4" />
                  Add Step
                </Button>
              </div>

            {steps.map((step, index) => (
              <Card key={index}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Mail className="w-4 h-4" />
                      Step {step.step_number}
                    </CardTitle>
                    {steps.length > 1 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeStep(index)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {index > 0 && (
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Delay (Days)</Label>
                        <Input
                          type="number"
                          min="0"
                          value={step.delay_days}
                          onChange={(e) =>
                            updateStep(index, "delay_days", parseInt(e.target.value) || 0)
                          }
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Delay (Hours)</Label>
                        <Input
                          type="number"
                          min="0"
                          max="23"
                          value={step.delay_hours}
                          onChange={(e) =>
                            updateStep(index, "delay_hours", parseInt(e.target.value) || 0)
                          }
                        />
                      </div>
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label>Email Subject</Label>
                    <Input
                      placeholder="Enter email subject..."
                      value={step.subject || ""}
                      onChange={(e) => updateStep(index, "subject", e.target.value)}
                    />
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>Email Body</Label>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setPreviewStep(previewStep === index ? null : index)}
                      >
                        <Eye className="w-3 h-3 mr-1" />
                        {previewStep === index ? "Hide Preview" : "Preview"}
                      </Button>
                    </div>
                    <RichTextEditor
                      value={step.body || ""}
                      onChange={(value) => updateStep(index, "body", value)}
                      placeholder="Enter email content... Use {{contact_name}}, {{company_name}}, etc. for personalization"
                    />
                    {previewStep === index && (
                      <div className="border rounded-lg p-4 bg-muted/30 mt-2">
                        <p className="text-xs text-muted-foreground mb-2">Preview:</p>
                        <div 
                          className="prose prose-sm max-w-none"
                          dangerouslySetInnerHTML={{ __html: step.body || "" }}
                        />
                      </div>
                    )}
                    <p className="text-xs text-muted-foreground">
                      Tip: Use merge tags like {`{{contact_name}}`}, {`{{company_name}}`} for personalization
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={loading}>
              {loading ? "Saving..." : "Save Sequence"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
};
